import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import axios from 'axios';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {addCommas, addFromColumn, parseCurrency, sortCurrency, sortNumber, toCurrency} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import Config from '../messages';

/**
 * Class for retrieving and displaying VIP Usage Summary in a single table
 *
 */
class VIPUsageReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allowLocationSelection: true,
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Location ID', key: 'location_id'},
                {label: 'Location Name', key: 'location_name'},
                {label: 'State', key: 'state'},
                {label: 'DBA', key: 'dba_name'},
                {label: 'VIP Usage', key: 'vip_usage'},
                {label: 'Comp Usage %', key: 'comp_usage'},
            ],
            detailLocation: {
                id: null,
                name: null,
                dba_name: null,
                selected: false,
                city: null,
                state: null,
            },
            detailLoading: false,
            message: null,
            reportName: 'VIP Usage Reports',
            summaryComplete: false,
            detailComplete: false,
            detailList: [],
            detailTableData: [],
            detailTableHeaders: [
                {label: 'VIP Customer ID', key: 'customer_id'},
                {label: 'First Name', key: 'first_name'},
                {label: 'Last Name', key: 'last_name'},
                {label: 'Email', key: 'email'},
                {label: 'Phone Number', key: 'telephone'},
                {label: 'City', key: 'city'},
                {label: 'State', key: 'state'},
                {label: 'Zipcode', key: 'zip'},
                {label: 'Location Registered', key: 'registered_by_client'},
                {label: 'Active', key: 'active'},
                {label: '$ Spent that Earned Points', key: 'amount_spent_that_earned_points'},
                {label: '$ Refunded that Returned Points', key: 'amount_refunded_that_returned_points'},
                {label: '$ Net Amount', key: 'net_amount'},
                {label: 'Points Spent', key: 'pts_spent'},
                {label: 'Number of Sales', key: 'num_sales'}
            ],
        };
        this.detailPosition = null;
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.daysBack !== this.props.daysBack) {
            this.fetchData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get most recent VIP Usage Report.
     *
     */
    fetchData(locationId) {
        // Assemble the URI
        const params = {};
        // Assemble the URI
        let resource;
        if (locationId) {
            params.location_id = locationId;
            params.daysBack = this.props.daysBack;
            resource = reportsResources.VIPUsageByLocation.GET_LIST(params);
        } else {
            params.daysBack = this.props.daysBack;
            resource = reportsResources.VIPUsage.GET_LIST(params);
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(locationId, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                detailLoading: false,
                message: message,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param locationId
     * @param data
     */
    processData(locationId, data) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        let detailList = [];
        if (data.aggregations) {
            if (!locationId) {
                if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                    let results = data.aggregations.by_location.buckets;
                    let resultsTotal = results.length;
                    let salesTotal = data.hits.total.value;
                    if (resultsTotal) {
                        // Assemble data for each location key
                        for (let s = 0; s < resultsTotal; s++) {
                            let locationResult = results[s];
                            let sales = locationResult.num_sales.value;
                            let locationSource = locationResult.column_name.hits.hits[0]._source.rxtxMsg;
                            if (locationSource) {
                                let location = locationResult.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                                // Create a row for each Location
                                const locationRow = {
                                    location_id: (location.id) ? location.id : '----',
                                    location_name: (location.name) ? location.name : '----',
                                    state: (location.state) ? location.state : '(None)',
                                    dba_name: (location.dba.name) ? location.dba.name : '(None)',
                                    vip_usage: (sales) ? sales : '----',
                                    comp_usage: ((sales / salesTotal) * 100).toFixed(2) + ' %',

                                };
                                summaryList.push(locationRow);
                            }
                        }
                    } else {
                        // No results
                    }
                } else {
                    // Structure does not conform
                }
                // Finalize table data
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true,
                }, () => {
                    this.props.onSummaryComplete(true);
                });
            }

            if (locationId) {
                if (data.aggregations.by_loyalty_customer_id && data.aggregations.by_loyalty_customer_id.buckets) {
                    let results = data.aggregations.by_loyalty_customer_id.buckets;
                    let resultsTotal = results.length;
                    if (resultsTotal) {
                        for (let x = 0; x < resultsTotal; x++) {
                            let locationResult = results[x];
                            let customerId = locationResult.key;
                            let numbSales = locationResult.num_of_sales.value;
                            let earnedPts = locationResult.payments.subtotal_that_earned_pts.payment_subtotal.value;
                            let earnedPtsToCurrency = toCurrency(earnedPts / 100);
                            let totalPts = locationResult.payments.pts_spent.payment_subtotal.value;
                            let returnedPts = locationResult.credits.refunds_that_return_pts.credits_subtotal.value;
                            let returnedPtsToCurrency = toCurrency(returnedPts / 100);

                            let loyaltyCustomerData = locationResult.column_name.hits.hits[0]._source.rxtxMsg.payload.loyalty_customer;
                            const detailRow = {
                                customer_id: (customerId) ? customerId : '----',
                                first_name: (loyaltyCustomerData.first_name) ? loyaltyCustomerData.first_name : '----',
                                last_name: (loyaltyCustomerData.last_name) ? loyaltyCustomerData.last_name : '----',
                                city: (loyaltyCustomerData.city) ? loyaltyCustomerData.city : '----',
                                state: (loyaltyCustomerData.state) ? loyaltyCustomerData.state : '----',
                                zip: (loyaltyCustomerData.zip) ? loyaltyCustomerData.zip : '----',
                                telephone: (loyaltyCustomerData.telephone) ? loyaltyCustomerData.telephone : '----',
                                email: (loyaltyCustomerData.email) ? loyaltyCustomerData.email : '----',
                                num_sales: (numbSales) ? numbSales : '----',
                                registered_by_client: (loyaltyCustomerData.registered_by_client && loyaltyCustomerData.registered_by_client.location) ? loyaltyCustomerData.registered_by_client.location : '----',
                                active: (loyaltyCustomerData.active) ? 'TRUE' : 'FALSE',
                                amount_spent_that_earned_points: (earnedPts) ? earnedPtsToCurrency : emptyCurrency,
                                amount_refunded_that_returned_points: (returnedPts) ? returnedPtsToCurrency : emptyCurrency,
                                net_amount: (returnedPts) ? toCurrency(parseCurrency(earnedPtsToCurrency) - parseCurrency(returnedPtsToCurrency)) : emptyCurrency,
                                pts_spent: (totalPts) ? (toCurrency(totalPts / 100)) : emptyCurrency


                            };
                            detailList.push(detailRow);
                            // console.log(detailList);
                        }
                    }
                }
                // Finalize table data
                this.setState({
                    detailComplete: true,
                    detailList: detailList,
                    detailLoading: false,
                    detailTableData: detailList
                });
            }
        } else {
            this.setState({
                detailLoading: false,
                summaryList: summaryList,
                summaryTableData: summaryList,
                summaryComplete: true,
            }, () => {
                this.props.onSummaryComplete(true);
            });
        }
    }

    /* Table Building ------------------------------ */
    handleLocationSelect(event, row) {
        const location = {
            id: row.location_id,
            name: row.location_name,
            selected: true,
            city: row.city,
            state: row.state,
            dba_name: row.dba_name
        };
        this.setState({
            allowLocationSelection: true,
            detailLocation: location,
            detailLoading: true
        }, () => {
            this.fetchData(location.id);
        });
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param tableName
     * @param table
     */
    handleSortOrFilterChange(event, tableName, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            if (tableData && tableData.length > 0) {
                if (tableName === 'detail') {
                    this.setState({
                        detailTableData: tableData
                    });
                } else {
                    this.setState({
                        summaryTableData: tableData
                    });
                }
            }
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            system
        } = this.props;

        const {
            allowLocationSelection,
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            detailComplete,
            detailLoading,
            detailTableData,
            detailTableHeaders,
            detailLocation,
            detailList,
        } = this.state;

        const defaultSort = setDefaultTableSort('location_id');
        const defaultDetailSort = setDefaultTableSort('customer_id');

        const iconStyle = (allowLocationSelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;
        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Location ID',
                        accessor: 'location_id',
                        style: tableStyles.right,
                        Cell: row => (
                            <span>
                                <span>{row.row.location_id}</span>
                                <DetailIcon
                                    onClick={(e) => this.handleLocationSelect(e, row.row)}
                                    style={iconStyle}
                                />
                            </span>
                        ),
                    },
                    {
                        Header: 'Location Name',
                        accessor: 'location_name',
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                    },
                    {
                        Header: 'DBA',
                        accessor: 'dba_name',
                    },
                    {
                        Header: 'VIP Usage',
                        accessor: 'vip_usage',
                        filterable: false,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'vip_usage'))}
                            </span>
                        ),
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    },
                    {
                        Header: 'Comp Usage %',
                        accessor: 'comp_usage',
                        filterable: false,
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: {...tableStyles.tdMax, ...tableStyles.right},
                    }
                ]
            }
        ];
        const tableDetailsColumns = [
            {
                Header: 'Customer',
                columns: [
                    {
                        Header: () => (<span>VIP<br/>Customer ID</span>),
                        accessor: 'customer_id',
                        width: 170
                    },
                    {
                        Header: () => (<span>First<br/>Name</span>),
                        accessor: 'first_name',
                    },
                    {
                        Header: () => (<span>Last<br/>Name</span>),
                        accessor: 'last_name',
                    },
                    {
                        Header: 'Email',
                        accessor: 'email',
                        width: 200
                    },
                    {
                        Header: () => (<span>Phone<br/>Number</span>),
                        accessor: 'telephone',
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Zipcode',
                        accessor: 'zip',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: () => (<span>Location<br/>Registered</span>),
                        accessor: 'registered_by_client',
                    },
                    {
                        Header: 'Active',
                        accessor: 'active',
                        filterable: false,
                        headerStyle: tableStyles.thLast,
                    }
                ]
            },
            {
                Header: 'Transactions',
                columns: [
                    {
                        Header: () => (<span>$ Spent<br/>that<br/>Earned<br/>Points</span>),
                        accessor: 'amount_spent_that_earned_points',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(detailTableData, 'amount_spent_that_earned_points'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>$ Refunded<br/>that<br/>Returned<br/>Points</span>),
                        accessor: 'amount_refunded_that_returned_points',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(detailTableData, 'amount_refunded_that_returned_points'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>$ Net<br/>Amount</span>),
                        accessor: 'net_amount',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(detailTableData, 'net_amount'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Points<br/>Spent</span>),
                        accessor: 'pts_spent',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(detailTableData, 'pts_spent'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Number<br/>of sales</span>),
                        accessor: 'num_sales',
                        sortMethod: (a, b) => sortNumber(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addFromColumn(detailTableData, 'num_sales')}
                            </span>
                        )
                    }
                ]
            }
        ];

        // Assemble Location Detail information
        const locationCity = (detailLocation && detailLocation.city) ? detailLocation.city + ', ' : '';
        const locationState = (detailLocation && detailLocation.state) ? detailLocation.state + '.' : '';

        return (
            <div>
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: VIP Usage */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export VIP Usage */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'VIP-Usage-' + system + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export VIP Usage Report
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <span>VIP Usage Report</span>
                            </h3>
                        </div>

                        {/* Table: VIP Usage */}
                        <ReactTable
                            key="table-VIP-usage"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={5}
                            showPagination={true}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.reactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.reactTable)}
                            ref={(r) => this.reactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped"
                        />
                        <div ref={(ref) => this.detailPosition = ref}>
                        </div>
                    </div>
                }

                {detailLoading && !detailComplete &&
                    <div className="loader-container">
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }

                {detailComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Detail VIP Usage Logs (Selected Location) */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Detail VIP Usage Logs (Selected Location) */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={detailTableData}
                                        headers={detailTableHeaders}
                                        filename={'VIP-Usage-Logs-by-Location-Report-' + system + '-' + detailLocation.id + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export VIP Usage Logs by Location Report {detailLocation.id}
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <span>VIP Usage Logs by Location Report {detailLocation.id}</span>
                            </h3>
                            <div style={commonStyles.tableDetail}>
                                Location Name: {detailLocation.name}<br/>
                                DBA: {detailLocation.dba_name}<br/>
                                {locationCity}{locationState}
                            </div>
                        </div>

                        {/* Table: Recent VIP Usage Logs (Selected Location) */}
                        <ReactTable
                            key="table-VIP-Usage-logs-detail"
                            data={detailList}
                            columns={tableDetailsColumns}
                            defaultPageSize={10}
                            showPagination={true}
                            defaultSorted={defaultDetailSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                            ref={(r) => this.detailReactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped overflow no-print"
                        />
                        <ReactTable
                            key="table-VIP-Usage-logs-detail-print"
                            data={detailTableData}
                            columns={tableDetailsColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultDetailSort}
                            defaultSortDesc={true}
                            filterable
                            resizable={false}
                            minRows={1}
                            className="print-only"
                        />
                    </div>
                }
            </div>
        )
    }
}

VIPUsageReport.propTypes = {
    addField: PropTypes.bool,
    daysBack: PropTypes.string,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    system: PropTypes.string,
};

VIPUsageReport.defaultProps = {
    addField: true,
    daysBack: '30'
};

const enhance = compose(
    connect()
);

export default enhance(VIPUsageReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/VIPUsageReport.js